import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { api } from "@/lib/api";

/**
 * useDisplayCurrency
 *
 * Returns { code, symbol, rate, convert, loading } for the user's chosen
 * display currency. Now reads from CurrencyContext (instant toggle) instead
 * of the user profile.
 *
 * `rate` is the KES→display multiplier; `convert(kes)` returns the numeric
 * amount in the display currency.
 */
export function useDisplayCurrency() {
    const { user } = useAuth();
    const { currency } = useCurrency();
    // Prefer CurrencyContext (instant toggle), fall back to user profile
    const code = currency || (user?.preferred_display_currency || "KES").toUpperCase();
    const [rates, setRates] = useState({ KES: 1.0 });
    const [symbolMap, setSymbolMap] = useState({ KES: "KES", USD: "USD", EUR: "EUR" });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            if (!user) return;
            setLoading(true);
            try {
                const [rr, opts] = await Promise.all([
                    api.get("/fx/rates", { params: { base: "KES" } }),
                    api.get("/preferences/options"),
                ]);
                if (cancelled) return;
                setRates(rr.data?.rates || { KES: 1.0 });
                const sym = {};
                for (const c of opts.data?.currencies || []) sym[c.code] = c.symbol;
                setSymbolMap((prev) => ({ ...prev, ...sym }));
            } catch { /* fail-open — UI keeps showing KES */ }
            finally { if (!cancelled) setLoading(false); }
        }
        load();
        return () => { cancelled = true; };
    }, [user]);

    const rate = rates[code] ?? 1.0;
    const symbol = symbolMap[code] || code;

    const convert = useMemo(
        () => (kesAmount) => {
            const n = Number(kesAmount || 0);
            if (!Number.isFinite(n)) return 0;
            return n * rate;
        },
        [rate],
    );

    return { code, symbol, rate, convert, loading };
}

/**
 * Format a numeric amount in the target currency.
 */
export function formatDisplayAmount(amount, code, symbol) {
    const n = Number(amount || 0);
    if (!Number.isFinite(n)) return `${symbol || code} 0`;
    if (code === "KES") {
        return `${symbol || code} ${Math.round(n).toLocaleString()}`;
    }
    return `${symbol || code} ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
